import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { usePets } from '@/contexts/pets';
import AddPetForm from '@/components/pets/AddPetForm';
import { PawPrint } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/utils/logger';

interface AddPetStepProps {
  onPetAdded: (petId: string) => void;
}

/**
 * First step of the onboarding tour - Add a pet
 */
const AddPetStep: React.FC<AddPetStepProps> = ({ onPetAdded }) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { refetchPets } = usePets();

  const handlePetSuccess = async (petId?: string) => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);
      if (petId) {
        await refetchPets();
        logger.info('Pet added successfully during onboarding, id:', petId);
        onPetAdded(petId);
      }
    } catch (error) {
      logger.error('Error after adding pet during onboarding:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsProcessing(false);
      setIsFormVisible(false);
    }
  };

  return (
    <div className="flex flex-col items-center max-h-[calc(100vh-110px)]">
      {/* Fixed header section - more compact */}
      <div className="sticky top-0 bg-white z-10 pb-2 w-full flex flex-col items-center space-y-2">
        <h2 className="text-xl font-bold text-center pt-1">Welcome to PetDocument!</h2>
        
        <p className="text-center text-gray-600 text-sm px-4 leading-tight">
          Let's get started by adding your first pet. This will help you organize their documents and set reminders easily.
        </p>

        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <PawPrint className="h-8 w-8 text-primary" />
        </div>

        <h3 className="text-lg font-semibold">Add Your Pet</h3>
      </div>
      
      {/* Content section - scrollable only on mobile */}
      <div className="w-full sm:overflow-visible sm:max-h-none max-sm:overflow-y-auto max-sm:max-h-[370px] sm:py-4 py-2">
        {isFormVisible ? (
          <div className="w-full">
            <AddPetForm 
              onSuccess={handlePetSuccess}
              onCancel={() => setIsFormVisible(false)}
              isLoading={isProcessing}
            />
          </div>
        ) : (
          <Button 
            className="w-full" 
            onClick={() => setIsFormVisible(true)}
            disabled={isProcessing}
            size="sm"
          >
            Add a Pet
          </Button>
        )}
      </div>
    </div>
  );
};

export default AddPetStep;
